import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from '@mui/material'
import { useResizeDetector } from 'react-resize-detector'
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-pro'
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro'
// import MassiveModificationModal from '../../components/massiveModificationModal/MassiveModificationModal'
import { useTranslation } from 'react-i18next'
import {
  DayDataList,
  DayDataTypes
} from '../../../../api/models/dataList.model'
import RatesUtilityBar from '../../components/RatesUtilityBar'
import StickyBar from '../../components/StickyBar'
import AvailabilityLegendModal from './components/AvailabilityLegendModal'
import useModal from 'src/hooks/useModal'
import RatesDataGridContextProvider, {
  useRatesDataGridContext
} from './contexts/ratesDataGrid/RatesDataGridContext'
import FilterBar from './components/FilterBar'
import { ConfirmModal, CustomBackdrop } from 'src/components'
import DayListBar from './components/DayListBar'
import VirtualizedGrid from './components/VirtualizedGrid'
import RatesGridCol1CellRender from './components/RatesGridCol1CellRender'
import RatesGridCol2CellRender, {
  type DataTypeChangeResult
} from './components/RatesGridCol2CellRender'
import { dateUtils } from 'src/utils/date.util'
import RatesGridEditCellRender from './components/ratesGridEditCellRender/RatesGridEditCellRender'

import RefreshIcon from '@mui/icons-material/Refresh'
import MassiveModificationModal from '../../components/massiveModificationModal/MassiveModificationModal'
import {
  CapacityRow,
  ChannelRow,
  RateRow,
  RatesDataGridRow,
  RowType
} from './contexts/ratesDataGrid/RatesDataGridContext.types'
import {
  getScrollLeftAfterDelta,
  getScrollLeftAfterResize
} from './RatesDataGrid.scroll'
import {
  createHorizontalScrollSync,
  HorizontalScrollSync
} from './RatesDataGrid.scrollSync'
import { hasEditedDaysForRoomType } from './contexts/ratesDataGrid/RatesDataGridContext.state'

export type RatesRowType =
  | 'rate'
  | 'channel'
  | 'hotel'
  | 'roomType'
  | 'channelSkeleton'
  | 'rateSkeleton'
export interface RatesRowData {
  id: string
  filtered?: boolean
  fatherId?: string
  type: RatesRowType
  show?: boolean
  haveChildren?: boolean
  inherited?: boolean
  editable?: boolean
  dataType?: keyof DayDataTypes
  dayData?: DayDataList[]
  hotelId?: string
  roomTypeId?: string
  rateId?: string
  numberChannelList?: number
  numberCapacityList?: number
  roomTypeCapacityList?: string[]
  roomTypeCapacityId?: string
  inheritedDataList?: string[]
  [key: string]: any
}

export interface ChangedRatesRowData {
  dataType: keyof DayDataTypes
  value: any
  day: string
  row: RatesRowData
}

type GridApiWithVirtualScroller = GridApiPro & {
  virtualScrollerRef?: React.RefObject<HTMLDivElement>
}

// const getInBetweenDates = (dates?: ApiRateWindowStateDateRange) => {
//   if (dates == null) return []
//   const newDates = []
//   let currentDate = moment(dates.iniDate, 'DD-MM-YYYY')
//   const endDate = moment(dates.endDate, 'DD-MM-YYYY')

//   while (!currentDate.isAfter(endDate)) {
//     newDates.push(currentDate.clone())
//     currentDate = currentDate.add(1, 'day')
//   }

//   return newDates
// }

const RatesDataGridComponent: ReactFC = React.memo(() => {
  const {
    loading: ratesContextLoading,
    apiData,
    refreshData,
    rows,
    daysList,
    dateRange,
    editedDaysList,
    saveEditedData,
    editedDaysListOverLimit,
    changeRateRowDataType,
    discardRoomTypeEdits
  } = useRatesDataGridContext()

  const gridApiRef = useGridApiRef()
  const scrollableEl = useRef<HTMLDivElement>(null)
  const { ref: containerEl } = useResizeDetector()
  const { width: leftColumnWidth, ref: leftColumnEl } = useResizeDetector()
  const { width: rightColumnWidth, ref: rightColumnEl } = useResizeDetector()
  const { t } = useTranslation()
  const dataGridContainerEl = useRef<HTMLDivElement>()
  const previousCellWidth = useRef(0)
  const gridReady = useRef(false)
  const horizontalScrollSync = useRef<HorizontalScrollSync | null>(null)
  const gridScrollerElement = useRef<HTMLDivElement | null>(null)
  const gridScrollerCleanup = useRef<(() => void) | null>(null)
  const preservedDataTypeScrollTop = useRef<number | null>(null)
  const editedDaysListRef = useRef(editedDaysList)
  editedDaysListRef.current = editedDaysList

  const [isOverLimitModalOpen, setIsOverLimitModalOpen] = useState(false)
  const [pendingDataTypeChange, setPendingDataTypeChange] = useState<{
    row: RateRow | CapacityRow | ChannelRow
    dataType: keyof DayDataTypes
  } | null>(null)
  const [pendingDataTypeChangeSaving, setPendingDataTypeChangeSaving] =
    useState(false)

  // const tableScrollableEl = useRef<HTMLDivElement>()
  const [massiveModificationModalOpen, setMassiveModificationModalOpen] =
    React.useState<{
      open: boolean
      row?: RateRow | CapacityRow | ChannelRow
    }>({
      open: false,
      row: undefined
    })
  const {
    isModalOpen: isAvailabilityLegendModalOpen,
    toggleModal: toggleAvailabilityLegendModal
  } = useModal()

  const initialLoad = ratesContextLoading && !apiData

  const cellWidth = (rightColumnWidth ?? 0) / 14

  const handleRefresh = async () => {
    await refreshData()
  }

  const getGridScroller = useCallback(
    () =>
      (gridApiRef.current as GridApiWithVirtualScroller)
        .virtualScrollerRef?.current ?? null,
    [gridApiRef]
  )

  const bindGridScrollInteractions = useCallback(() => {
    const nextGridScroller = getGridScroller()
    if (nextGridScroller === gridScrollerElement.current) return

    gridScrollerCleanup.current?.()
    gridScrollerElement.current = nextGridScroller
    if (!nextGridScroller) {
      gridScrollerCleanup.current = null
      return
    }

    const beginGridInteraction = () =>
      horizontalScrollSync.current?.beginInteraction('grid')
    const endGridInteraction = () =>
      horizontalScrollSync.current?.endInteraction('grid')
    const passiveOptions = { passive: true }

    nextGridScroller.addEventListener(
      'wheel',
      beginGridInteraction,
      passiveOptions
    )
    nextGridScroller.addEventListener(
      'pointerdown',
      beginGridInteraction,
      passiveOptions
    )
    nextGridScroller.addEventListener(
      'touchstart',
      beginGridInteraction,
      passiveOptions
    )
    nextGridScroller.addEventListener(
      'scrollend',
      endGridInteraction,
      passiveOptions
    )

    gridScrollerCleanup.current = () => {
      nextGridScroller.removeEventListener('wheel', beginGridInteraction)
      nextGridScroller.removeEventListener(
        'pointerdown',
        beginGridInteraction
      )
      nextGridScroller.removeEventListener('touchstart', beginGridInteraction)
      nextGridScroller.removeEventListener('scrollend', endGridInteraction)
      if (gridScrollerElement.current === nextGridScroller) {
        gridScrollerElement.current = null
      }
    }
  }, [getGridScroller])

  useEffect(() => {
    const sync = createHorizontalScrollSync({
      getGridLeft: () =>
        getGridScroller()?.scrollLeft ??
        (gridReady.current
          ? gridApiRef.current.getScrollPosition().left
          : 0),
      getHeaderLeft: () => scrollableEl.current?.scrollLeft ?? 0,
      setGridLeft: (left) => {
        if (gridReady.current) gridApiRef.current.scroll({ left })
      },
      setHeaderLeft: (left) => {
        if (scrollableEl.current) scrollableEl.current.scrollLeft = left
      }
    })
    horizontalScrollSync.current = sync
    const unsubscribe = gridApiRef.current.subscribeEvent(
      'scrollPositionChange',
      ({ left }) =>
        sync.handleSourceScroll(
          'grid',
          getGridScroller()?.scrollLeft ?? left
        )
    )
    const headerScroller = scrollableEl.current
    const endHeaderInteraction = () => sync.endInteraction('header')
    headerScroller?.addEventListener('scrollend', endHeaderInteraction, {
      passive: true
    })
    bindGridScrollInteractions()

    return () => {
      unsubscribe()
      headerScroller?.removeEventListener('scrollend', endHeaderInteraction)
      gridScrollerCleanup.current?.()
      gridScrollerCleanup.current = null
      sync.dispose()
      if (horizontalScrollSync.current === sync) {
        horizontalScrollSync.current = null
      }
    }
  }, [bindGridScrollInteractions, getGridScroller, gridApiRef])

  const scrollGridTo = useCallback(
    (left: number) => {
      if (!gridReady.current) return
      horizontalScrollSync.current?.scrollTo(left)
    },
    []
  )

  const handleGridReady = useCallback(() => {
    gridReady.current = true
    bindGridScrollInteractions()
    horizontalScrollSync.current?.reconcile()
  }, [bindGridScrollInteractions])

  const handleHeaderScroll = useCallback(
    (left: number) => {
      horizontalScrollSync.current?.handleSourceScroll('header', left)
    },
    []
  )

  const handleHeaderScrollInteractionStart = useCallback(() => {
    horizontalScrollSync.current?.beginInteraction('header')
  }, [])

  const ensureHorizontalScrollAlignment = useCallback(() => {
    horizontalScrollSync.current?.ensureAligned()
  }, [])

  const handleScrollRequest = useCallback(
    (delta: number) => {
      if (!gridReady.current) return
      const { left } = gridApiRef.current.getScrollPosition()
      scrollGridTo(getScrollLeftAfterDelta(left, delta))
    },
    [gridApiRef, scrollGridTo]
  )

  const dateRangeKey = `${dateUtils.formatDateToString(
    dateRange[0]
  )}-${dateUtils.formatDateToString(dateRange[1])}`

  useEffect(() => {
    scrollGridTo(0)
  }, [dateRangeKey, scrollGridTo])

  useEffect(() => {
    const oldCellWidth = previousCellWidth.current
    if (
      gridReady.current &&
      oldCellWidth > 0 &&
      cellWidth > 0 &&
      oldCellWidth !== cellWidth
    ) {
      const { left } = gridApiRef.current.getScrollPosition()
      scrollGridTo(
        getScrollLeftAfterResize({
          left,
          previousCellWidth: oldCellWidth,
          nextCellWidth: cellWidth
        })
      )
    }
    previousCellWidth.current = cellWidth
  }, [cellWidth, gridApiRef, scrollGridTo])

  const handleSave = () => {
    if (editedDaysListOverLimit.length > 0) {
      openOverLimitConfirmModal()
    } else {
      saveEditedData()
    }
  }

  const handleConfirmSave = async () => {
    closeOverLimitConfirmModal()
    saveEditedData()
  }

  const handleCancelConfirm = () => {
    closeOverLimitConfirmModal()
  }

  const openOverLimitConfirmModal = () => {
    setIsOverLimitModalOpen(true)
  }

  const closeOverLimitConfirmModal = () => {
    setIsOverLimitModalOpen(false)
  }

  const applyDataTypeChange = useCallback(
    (row: RateRow | CapacityRow | ChannelRow, dataType: keyof DayDataTypes) => {
      changeRateRowDataType({ row, dataType })
    },
    [changeRateRowDataType]
  )

  const preserveVerticalScroll = useCallback(() => {
    if (!gridReady.current) return
    preservedDataTypeScrollTop.current =
      gridApiRef.current.getScrollPosition().top
  }, [gridApiRef])

  useLayoutEffect(() => {
    if (
      preservedDataTypeScrollTop.current == null ||
      !gridReady.current
    ) {
      return
    }

    gridApiRef.current.scroll({ top: preservedDataTypeScrollTop.current })
    preservedDataTypeScrollTop.current = null
  }, [gridApiRef, rows])

  const handleRequestDataTypeChange = useCallback(
    (
      row: RatesDataGridRow,
      dataType: keyof DayDataTypes
    ): DataTypeChangeResult => {
      if (!('dayDataType' in row) || row.dayDataType === dataType) {
        return 'unchanged'
      }
      const hasRoomTypeEdits = hasEditedDaysForRoomType(
        editedDaysListRef.current,
        row.data.roomTypeId
      )
      if (hasRoomTypeEdits) {
        setPendingDataTypeChange({ row, dataType })
        return 'confirmation-required'
      }
      preserveVerticalScroll()
      applyDataTypeChange(row, dataType)
      return 'applied'
    },
    [applyDataTypeChange, preserveVerticalScroll]
  )

  const handleSaveAndChangeDataType = async () => {
    if (!pendingDataTypeChange) return
    setPendingDataTypeChangeSaving(true)
    const saved = await saveEditedData()
    setPendingDataTypeChangeSaving(false)
    if (!saved) return
    preserveVerticalScroll()
    applyDataTypeChange(
      pendingDataTypeChange.row,
      pendingDataTypeChange.dataType
    )
    setPendingDataTypeChange(null)
  }

  const handleDiscardAndChangeDataType = () => {
    if (!pendingDataTypeChange) return
    discardRoomTypeEdits(pendingDataTypeChange.row.data.roomTypeId)
    preserveVerticalScroll()
    applyDataTypeChange(
      pendingDataTypeChange.row,
      pendingDataTypeChange.dataType
    )
    setPendingDataTypeChange(null)
  }

  const handleOpenCreateRatePriceModal = useCallback(
    (row: RateRow | CapacityRow | ChannelRow) => {
      setMassiveModificationModalOpen({
        open: true,
        row
      })
    },
    []
  )
  const handleOpenMassiveModificationModalOpen = () =>
    setMassiveModificationModalOpen({
      open: true
    })
  const handleCloseMassiveModificationModalOpen = () =>
    setMassiveModificationModalOpen({
      open: false
    })

  const haveChangedCells = Object.keys(editedDaysList).length > 0

  const columns: GridColDef[] = useMemo(
    () => [
      {
        width: (leftColumnWidth ?? 0) * 0.68,
        field: 'col1',
        headerName: 'Tipo 1 (Unidades totales 5)',
        resizable: false,
        editable: false,
        sortable: false,
        headerClassName: 'Avail-datagrid-header',
        renderCell: (params) => {
          return <RatesGridCol1CellRender cellParams={params} />
        }
      },
      {
        width: (leftColumnWidth ?? 0) * 0.32,
        field: 'col2',
        headerName: '',
        resizable: false,
        sortable: false,
        headerClassName: 'Avail-datagrid-header',
        cellClassName: () => 'ratesDataGridCol2',
        renderCell: (params) => {
          return (
            <RatesGridCol2CellRender
              cellParams={params}
              openCreateRatePriceModal={handleOpenCreateRatePriceModal}
              restrictionTypeList={apiData?.restrictionTypeList ?? []}
              onChangeDataType={handleRequestDataTypeChange}
            />
          )
        }
      },
      ...daysList.map(
        (columnDate): GridColDef => ({
          width: cellWidth,
          field: dateUtils.formatDateToString(columnDate),
          headerName: dateUtils.toDayString(columnDate),
          resizable: false,
          align: 'center',
          sortable: false,
          disableReorder: true,
          headerAlign: 'center',
          headerClassName: 'Avail-datagrid-header Avail-datagrid-header-bold',
          editable: false,
          cellClassName: (params) => {
            const row = params.row as RatesDataGridRow
            let customClassName = `cellData ${
              row.id
            } ratesDataGrid-row-${row.type.toLowerCase()}`
            const isFridayOrSaturday = dateUtils.isWeekend(columnDate)
            if (
              isFridayOrSaturday &&
              (row.type === RowType.RATE ||
                row.type === RowType.CHANNEL ||
                row.type === RowType.CAPACITY ||
                row.type === RowType.CAPACITY_SKELETON ||
                row.type === RowType.CHANNEL_SKELETON)
            ) {
              customClassName += ' weekendCell'
            }
            if (
              'editable' in row &&
              !row.editable &&
              (row.type === RowType.CHANNEL ||
                row.type === RowType.RATE ||
                row.type === RowType.CAPACITY)
            ) {
              customClassName += ' notEditableCell'
            }

            return customClassName
          },
          renderCell: (params) => {
            return (
              <RatesGridEditCellRender
                params={params}
                openLegendModal={toggleAvailabilityLegendModal}
                dayDate={columnDate}
                onBeforeEdit={ensureHorizontalScrollAlignment}
              />
            )
          }
        })
      )
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      gridApiRef,
      leftColumnWidth,
      cellWidth,
      apiData,
      daysList,
      handleOpenCreateRatePriceModal,
      handleRequestDataTypeChange,
      ensureHorizontalScrollAlignment,
      toggleAvailabilityLegendModal
    ]
  )

  return (
    <Box
      sx={{
        position: 'relative'
      }}
      ref={containerEl}
    >
      <StickyBar>
        <RatesUtilityBar>
          <FilterBar
            sx={{
              width: '70%'
            }}
          />
          <Box
            sx={{
              marginLeft: 'auto',
              width: '30%',
              textAlign: 'right',
              '& > *:not(:first-of-type)': {
                marginLeft: '1rem'
              }
            }}
          >
            <IconButton
              onClick={handleRefresh}
              sx={{
                justifySelf: 'right'
              }}
              disabled={ratesContextLoading}
            >
              <RefreshIcon />
            </IconButton>
            <Button
              onClick={() => handleOpenMassiveModificationModalOpen()}
              variant='outlined'
            >
              {t('apps.rates.rateDataGrid.labels.massive_update')}
            </Button>
            <Button
              variant='contained'
              disabled={!haveChangedCells}
              onClick={handleSave}
            >
              {t('apps.rates.rateDataGrid.labels.save')}
            </Button>
          </Box>
        </RatesUtilityBar>
        <DayListBar
          leftColumnEl={leftColumnEl}
          rightColumnEl={rightColumnEl}
          rightColumnWidth={rightColumnWidth}
          scrollableEl={scrollableEl}
          onScroll={handleHeaderScroll}
          onScrollInteractionStart={handleHeaderScrollInteractionStart}
          onScrollRequest={handleScrollRequest}
        />
      </StickyBar>
      <Box
        ref={dataGridContainerEl}
        sx={{
          position: 'relative',
          marginTop: '10px',
          borderRadius: 2,
          overflow: 'hidden',
          width: {
            xs: '100%',
            xl: `${(leftColumnWidth ?? 1440) + cellWidth * 14}px`
          },
          height: 800,
          '@media screen and (min-height: 750px)': {
            height: '1080px'
          },
          '@media screen and (min-height: 1080px)': {
            height: '1200px'
          }
        }}
      >
        <VirtualizedGrid
          gridApiRef={gridApiRef}
          rows={rows}
          columns={columns}
          containerEl={dataGridContainerEl.current}
          onReady={handleGridReady}
        />
      </Box>
      {!initialLoad && (
        <MassiveModificationModal
          open={massiveModificationModalOpen}
          handleClose={handleCloseMassiveModificationModalOpen}
        />
      )}

      <AvailabilityLegendModal
        open={isAvailabilityLegendModalOpen}
        onClose={toggleAvailabilityLegendModal}
      />
      <ConfirmModal
        body={
          <Box>
            <Typography>
              {t('alerts.more_than_limit_day_value_in_fields', {
                value: '10.000€',
                ns: 'tarifas'
              })}
            </Typography>
            {editedDaysListOverLimit?.map(({ date, row }) => {
              const dateString = dateUtils.formatDateToString(date)
              return (
                <Box key={`${row.id}-${Object}`}>
                  <Typography>{`${t('labels.Day')} ${dateString} ${
                    row.data.name
                  }`}</Typography>
                </Box>
              )
            })}
          </Box>
        }
        open={isOverLimitModalOpen}
        onConfirm={handleConfirmSave}
        onClose={handleCancelConfirm}
        loading={ratesContextLoading}
        cancelLabel={t('labels.Keep_editing')}
      />
      <Dialog
        open={pendingDataTypeChange != null}
        onClose={() => {
          if (!pendingDataTypeChangeSaving) setPendingDataTypeChange(null)
        }}
      >
        <DialogTitle>{t('labels.ATTENTION!')}</DialogTitle>
        <DialogContent>
          <Typography>{t('alerts.pending_rate_edits')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={pendingDataTypeChangeSaving}
            onClick={() => setPendingDataTypeChange(null)}
          >
            {t('buttons.Cancel')}
          </Button>
          <Button
            disabled={pendingDataTypeChangeSaving}
            color='warning'
            onClick={handleDiscardAndChangeDataType}
          >
            {t('buttons.Discard_and_change')}
          </Button>
          <Button
            disabled={pendingDataTypeChangeSaving}
            variant='contained'
            onClick={handleSaveAndChangeDataType}
          >
            {t('buttons.Save_and_change')}
          </Button>
        </DialogActions>
      </Dialog>
      {ratesContextLoading && <CustomBackdrop />}
    </Box>
  )
})

const RatesDataGrid = () => {
  return (
    <RatesDataGridContextProvider>
      <RatesDataGridComponent />
    </RatesDataGridContextProvider>
  )
}

export default React.memo(RatesDataGrid)
